/**
 * Teams API Hook
 * 
 * Provides hooks for managing teams with demo/live mode support
 * Handles API integration when backend is available
 */

import { useState, useCallback } from 'react';
import { useMode } from '@/providers/ModeProvider';
import { demoTeams } from '@/data/demo-teams';
import type { Team, TeamCreateDto, TeamFilters } from '@/types/teams';
import { getApiUrl } from '@/lib/api-config';
import { getBackendUrl } from '@/hooks/use-get-backend-url';
import { getAuthConfig } from '@/lib/auth-fetch';

/**
 * Get the API base URL using the same logic as DataService.
 * Checks configured URL, env variable, and backend URL detection.
 */
const getApiBaseUrl = (): string => {
  const apiUrlFromConfig = getApiUrl();
  const envUrl = import.meta.env.VITE_API_BASE_URL;
  const backendUrl = getBackendUrl();
  const isE2B = typeof window !== 'undefined' && window.location.hostname.includes('.e2b.app');

  // In E2B environment, always use the detected backend URL
  // Otherwise, use the configured URL or env URL as fallback
  const baseUrl = isE2B ? backendUrl : (apiUrlFromConfig ?? envUrl ?? backendUrl);
  return baseUrl.replace(/\/$/, '');
};

/**
 * Build headers for API requests including auth token and instance ID.
 */
const buildHeaders = async (additionalHeaders?: Record<string, string>): Promise<Record<string, string>> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...additionalHeaders,
  };

  const authConfig = getAuthConfig();
  if (authConfig.getAccessToken) {
    try {
      const token = await authConfig.getAccessToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('Error getting access token:', error);
    }
  }

  headers['X-Instance-Id'] = authConfig.instanceId ?? 'default-instance';
  return headers;
};

export function useTeams() {
  const { isDemo } = useMode();
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Load teams based on mode
  const loadTeams = useCallback(async (filters?: TeamFilters) => {
    setLoading(true);
    setError(null);

    try {
      if (isDemo) {
        // Demo mode - use local data
        await new Promise(resolve => setTimeout(resolve, 300));
        let filteredTeams = [...demoTeams];

        // Apply filters
        if (filters?.search) {
          const search = filters.search.toLowerCase();
          filteredTeams = filteredTeams.filter(
            team =>
              team.name.toLowerCase().includes(search) ||
              team.description?.toLowerCase().includes(search)
          );
        }

        setTeams(filteredTeams);
      } else {
        // Live mode - fetch from API
        const queryParams = new URLSearchParams();
        if (filters?.search) queryParams.append('search', filters.search);

        const headers = await buildHeaders();
        const response = await fetch(
          `${getApiBaseUrl()}/api/teams?${queryParams.toString()}`,
          { headers }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch teams from API');
        }

        const data = await response.json();
        setTeams(data);
      }
    } catch (err) {
      console.error('Error loading teams:', err);
      setError(err instanceof Error ? err : new Error('Unknown error'));
      // Fallback to demo data on error
      setTeams(demoTeams);
    } finally {
      setLoading(false);
    }
  }, [isDemo]);

  // Create team
  const createTeam = useCallback(
    async (data: TeamCreateDto): Promise<Team> => {
      if (isDemo) {
        // Demo mode - simulate creation
        await new Promise(resolve => setTimeout(resolve, 800));

        const newTeam: Team = {
          id: `team_${Date.now()}`,
          ...data,
          isDefault: false,
          canDelete: true,
          canRename: true,
          memberCount: 0,
          permissions: [],
          createdAt: new Date().toISOString(),
          createdBy: 'current_user',
          updatedAt: new Date().toISOString(),
          updatedBy: 'current_user',
        };

        setTeams(prev => [...prev, newTeam]);
        return newTeam;
      } else {
        // Live mode - create via API
        const headers = await buildHeaders();
        const response = await fetch(`${getApiBaseUrl()}/api/teams`, {
          method: 'POST',
          headers,
          body: JSON.stringify(data),
        });

        if (!response.ok) {
          throw new Error('Failed to create team');
        }

        const newTeam = await response.json();
        setTeams(prev => [...prev, newTeam]);
        return newTeam;
      }
    },
    [isDemo]
  );

  // Update team
  const updateTeam = useCallback(
    async (teamId: string, updates: Partial<Team>): Promise<void> => {
      if (isDemo) {
        // Demo mode - simulate update
        await new Promise(resolve => setTimeout(resolve, 500));

        setTeams(prev =>
          prev.map(team =>
            team.id === teamId
              ? {
                  ...team,
                  ...updates,
                  updatedAt: new Date().toISOString(),
                  updatedBy: 'current_user',
                }
              : team
          )
        );
      } else {
        // Live mode - update via API
        const headers = await buildHeaders();
        const response = await fetch(`${getApiBaseUrl()}/api/teams/${teamId}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(updates),
        });

        if (!response.ok) {
          throw new Error('Failed to update team');
        }

        const updatedTeam = await response.json();
        setTeams(prev =>
          prev.map(team => (team.id === teamId ? updatedTeam : team))
        );
      }
    },
    [isDemo]
  );

  // Delete team
  const deleteTeam = useCallback(
    async (teamId: string): Promise<void> => {
      if (isDemo) {
        // Demo mode - simulate deletion
        await new Promise(resolve => setTimeout(resolve, 500));
        setTeams(prev => prev.filter(team => team.id !== teamId));
      } else {
        // Live mode - delete via API
        const headers = await buildHeaders();
        const response = await fetch(`${getApiBaseUrl()}/api/teams/${teamId}`, {
          method: 'DELETE',
          headers,
        });

        if (!response.ok) {
          throw new Error('Failed to delete team');
        }

        setTeams(prev => prev.filter(team => team.id !== teamId));
      }
    },
    [isDemo]
  );

  // Delete multiple teams
  const deleteTeams = useCallback(
    async (teamIds: string[]): Promise<void> => {
      if (isDemo) {
        // Demo mode - simulate bulk deletion
        await new Promise(resolve => setTimeout(resolve, 500));
        setTeams(prev => prev.filter(team => !teamIds.includes(team.id)));
      } else {
        // Live mode - bulk delete via API
        const headers = await buildHeaders();
        const response = await fetch(`${getApiBaseUrl()}/api/teams/bulk-delete`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ teamIds }),
        });

        if (!response.ok) {
          throw new Error('Failed to delete teams');
        }

        setTeams(prev => prev.filter(team => !teamIds.includes(team.id)));
      }
    },
    [isDemo]
  );

  // Get team by ID
  const getTeam = useCallback(
    async (teamId: string): Promise<Team | null> => {
      if (isDemo) {
        // Demo mode - find in local data
        return teams.find(team => team.id === teamId) ?? null;
      } else {
        // Live mode - fetch from API
        const headers = await buildHeaders();
        const response = await fetch(`${getApiBaseUrl()}/api/teams/${teamId}`, {
          headers,
        });

        if (!response.ok) {
          throw new Error('Failed to fetch team');
        }

        return await response.json();
      }
    },
    [isDemo, teams]
  );

  // Update team permissions
  const updateTeamPermissions = useCallback(
    async (teamId: string, grantedPermissions: string[]): Promise<void> => {
      if (isDemo) {
        // Demo mode - simulate update
        await new Promise(resolve => setTimeout(resolve, 500));
        console.log('Demo mode: Would update permissions for team', teamId, 'with', grantedPermissions);
      } else {
        // Live mode - update via API
        const headers = await buildHeaders();
        const response = await fetch(`${getApiBaseUrl()}/api/teams/${teamId}/permissions`, {
          method: 'PUT',
          headers,
          body: JSON.stringify({ grantedPermissions }),
        });

        if (!response.ok) {
          throw new Error('Failed to update team permissions');
        }

        // Reload teams to get updated data
        await loadTeams();
      }
    },
    [isDemo, loadTeams]
  );

  return {
    teams,
    loading,
    error,
    loadTeams,
    createTeam,
    updateTeam,
    deleteTeam,
    deleteTeams,
    getTeam,
    updateTeamPermissions,
  };
}
